import {
  formatArea,
  formatDistance,
} from "@/components/common/settings-context/distance-unit-utils";
import { assert, type DistanceUnitTypes } from "@faro-lotv/foundation";
import jsPDF from "jspdf";
import { Vector3 } from "three";
import { CYLINDER_DEVIATION_COLORS } from "./cylinder-surface";
import { surfaceDimensions } from "./depth-surface";
import { SURFACE_REPORT_IMAGE_SIZE } from "./surface-report-camera";
import type { SurfaceSelection } from "./surface-selection-pass";

export const SURFACE_REPORT_TITLE = "Surface selection report";
export const SURFACE_REPORT_LOGO =
  "https://cdn.bfldr.com/4P6TP155/at/2mgt87rmjc8sttbjccc62fkx/Sphere_XG_Logo_-_Color_-_RGB.png?auto=webp&format=png";

export type SurfaceReportEntry = {
  title: string;
  dimensions: Array<{ label: string; value: string }>;
  tolerance?: string;
  coverage: string;
  link: string;
  isolated: string;
  context: string;
  cylinderAnalysis?: string;
  overviewMap?: string;
  mapCaption?: string;
};

export type SurfaceReportData = {
  projectName: string;
  date: string;
  entries: SurfaceReportEntry[];
};

export type SurfaceReportStatus =
  | { phase: "idle" }
  | { phase: "loading"; completed: number; total: number }
  | { phase: "error"; message: string }
  | { phase: "ready"; report: SurfaceReportData; pdf: Blob };

/**
 * @param selection Current selection.
 * @returns An independent snapshot, including all mutable geometry and matrices.
 */
export function snapshotSurfaceSelection(
  selection: SurfaceSelection,
): SurfaceSelection {
  return {
    ...selection,
    depth: selection.depth.slice(),
    mask: selection.mask.slice(),
    positions: selection.positions.slice(),
    outline: selection.outline.slice(),
    endCaps: selection.endCaps?.slice(),
    centerline: selection.centerline?.slice(),
    rings: selection.rings?.slice(),
    cylinderSamples: selection.cylinderSamples?.slice(),
    inverseViewProjection: selection.inverseViewProjection.clone(),
    viewProjection: selection.viewProjection.clone(),
    plane: selection.plane.clone(),
    origin: selection.origin.clone(),
    cylinder: selection.cylinder
      ? {
          ...selection.cylinder,
          center: selection.cylinder.center.clone(),
          axis: selection.cylinder.axis.clone(),
          radial: selection.cylinder.radial.clone(),
        }
      : undefined,
  };
}

/**
 * @param selection Immutable selection snapshot.
 * @param index Display order.
 * @param up Viewer world-up direction.
 * @param unit Report measurement unit.
 * @param images Matched isolated/context orthophotos.
 * @param link Camera deep link.
 * @returns The same content used by both preview and PDF.
 */
export function surfaceReportEntry(
  selection: SurfaceSelection,
  index: number,
  up: Vector3,
  unit: DistanceUnitTypes,
  images: {
    isolated: string;
    context: string;
    cylinderAnalysis?: string;
    overviewMap?: string;
    mapCaption?: string;
  },
  link: string,
): SurfaceReportEntry {
  const dimensions = surfaceDimensions(
    selection.positions,
    new Vector3(selection.plane.x, selection.plane.y, selection.plane.z),
    up,
    selection.worldSpace ? selection : undefined,
  );
  assert(dimensions, "The selection has no measurable surface");
  const coverage =
    selection.coverage === "complete" ? "Complete" : "Partial selection";
  return {
    ...images,
    link,
    coverage,
    title: `${selection.cylinder ? "Cylinder" : "Plane"} ${index + 1}`,
    dimensions: selection.cylinder
      ? [
          { label: "Length", value: formatDistance(dimensions.height, unit) },
          { label: "Diameter", value: formatDistance(dimensions.width, unit) },
        ]
      : [
          { label: "Width", value: formatDistance(dimensions.width, unit) },
          { label: "Height", value: formatDistance(dimensions.height, unit) },
          { label: "Surface area", value: formatArea(dimensions.area, unit) },
        ],
    tolerance: selection.cylinder
      ? formatDistance(selection.tolerance, unit)
      : undefined,
  };
}

const PAGE = {
  margin: 15,
  footer: 12,
  titleSize: 16,
  bodySize: 10,
  imageGap: 8,
  maxPhotoHeight: 82,
  logoWidth: 28,
  headingGap: 14,
  ruleColor: 150,
  reservedCaptions: 23,
  linkGreen: 80,
  mapProjectOffset: 13,
  mapImageOffset: 22,
};

/**
 * @param report Frozen report entries shared with the preview.
 * @param format Regional paper format, matching existing reports.
 * @param logo Optional downloaded Sphere logo; text branding is used offline.
 * @returns A frontend-generated PDF with both images and a camera link per selection.
 */
export function createSurfaceReportPdf(
  report: SurfaceReportData,
  format: "a4" | "letter",
  logo?: Uint8Array,
): jsPDF {
  assert(report.entries.length > 0, "A report needs at least one selection");
  const pdf = new jsPDF("p", "mm", format, true);
  pdf.setProperties({
    title: SURFACE_REPORT_TITLE,
    subject: report.projectName,
    creator: "FARO Sphere XG",
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PAGE.margin * 2;
  const pageCount = report.entries.reduce(
    (count, entry) =>
      count +
      1 +
      (entry.overviewMap ? 1 : 0) +
      (entry.cylinderAnalysis ? 1 : 0),
    0,
  );
  let pageNumber = 0;
  report.entries.forEach((entry, index) => {
    if (index) pdf.addPage();
    let vertical = PAGE.margin;
    pdf.setTextColor(30);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(PAGE.titleSize);
    if (logo) {
      const size = pdf.getImageProperties(logo);
      pdf.addImage(
        logo,
        "PNG",
        PAGE.margin,
        vertical - 5,
        PAGE.logoWidth,
        (PAGE.logoWidth * size.height) / size.width,
      );
    } else {
      pdf.text("FARO Sphere XG", PAGE.margin, vertical);
    }
    vertical += PAGE.headingGap;
    pdf.text(SURFACE_REPORT_TITLE, PAGE.margin, vertical);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(PAGE.bodySize);
    vertical += 7;
    const project: string[] = pdf.splitTextToSize(
      report.projectName,
      contentWidth,
    );
    pdf.text(project, PAGE.margin, vertical);
    vertical += Math.max(1, project.length) * 4 + 2;
    pdf.setTextColor(90);
    pdf.text(`Date: ${report.date}`, PAGE.margin, vertical);
    vertical += 7;
    pdf.setDrawColor(PAGE.ruleColor);
    pdf.line(PAGE.margin, vertical, pageWidth - PAGE.margin, vertical);
    vertical += 7;
    pdf.setTextColor(30);
    pdf.setFont("helvetica", "bold");
    pdf.text(`${entry.title} - ${entry.coverage}`, PAGE.margin, vertical);
    pdf.setFont("helvetica", "normal");
    vertical += 6;
    const metrics: string[] = pdf.splitTextToSize(
      entry.dimensions
        .map(({ label, value }) => `${label}: ~${value}`)
        .join("     "),
      contentWidth,
    );
    pdf.text(metrics, PAGE.margin, vertical);
    vertical += metrics.length * 4 + 3;
    if (entry.tolerance) {
      pdf.text(
        `Radial deviation: -${entry.tolerance} to +${entry.tolerance}`,
        PAGE.margin,
        vertical,
      );
      vertical += 5;
      const labels = ["Inside", "Near fit", "Outside"];
      Object.values(CYLINDER_DEVIATION_COLORS).forEach((color, colorIndex) => {
        const horizontal = PAGE.margin + colorIndex * 45;
        pdf.setFillColor(color);
        pdf.rect(horizontal, vertical - 3, 5, 3, "F");
        pdf.text(labels[colorIndex], horizontal + 7, vertical);
      });
      vertical += 6;
    }
    const photoHeight = Math.min(
      PAGE.maxPhotoHeight,
      (pageHeight - vertical - PAGE.footer - PAGE.reservedCaptions) / 2,
    );
    assert(photoHeight > 0, "Report metadata exceeds the page size");
    const photoWidth = Math.min(
      contentWidth,
      (photoHeight * SURFACE_REPORT_IMAGE_SIZE.width) /
        SURFACE_REPORT_IMAGE_SIZE.height,
    );
    const actualHeight =
      (photoWidth * SURFACE_REPORT_IMAGE_SIZE.height) /
      SURFACE_REPORT_IMAGE_SIZE.width;
    for (const [label, image] of [
      ["Isolated selection", entry.isolated],
      ["In context", entry.context],
    ]) {
      pdf.text(label, PAGE.margin, vertical);
      vertical += 3;
      pdf.addImage(
        image,
        "PNG",
        (pageWidth - photoWidth) / 2,
        vertical,
        photoWidth,
        actualHeight,
        undefined,
        "FAST",
      );
      vertical += actualHeight + PAGE.imageGap;
    }
    pdf.setTextColor(0, PAGE.linkGreen, 180);
    pdf.textWithLink(
      "Open orthographic view in FARO Sphere XG",
      PAGE.margin,
      pageHeight - PAGE.footer - 5,
      { url: entry.link },
    );
    pdf.setTextColor(100);
    pdf.setFontSize(8);
    pdf.text(
      `Approximate measurements. Page ${++pageNumber} of ${pageCount}`,
      PAGE.margin,
      pageHeight - PAGE.footer,
    );
    if (entry.cylinderAnalysis) {
      pdf.addPage();
      pdf.setTextColor(30);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(PAGE.titleSize);
      pdf.text(
        `${entry.title} - Radial deviation`,
        PAGE.margin,
        PAGE.margin + 5,
      );
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(PAGE.bodySize);
      const projectLines: string[] = pdf.splitTextToSize(
        report.projectName,
        contentWidth,
      );
      pdf.text(projectLines, PAGE.margin, PAGE.margin + PAGE.mapProjectOffset);
      const analysisTop =
        PAGE.margin + PAGE.mapImageOffset + projectLines.length * 4;
      pdf.text(
        "Axial orthographic view and deviation histogram",
        PAGE.margin,
        analysisTop,
      );
      pdf.addImage(
        entry.cylinderAnalysis,
        "PNG",
        PAGE.margin,
        analysisTop + 5,
        contentWidth,
        (contentWidth * SURFACE_REPORT_IMAGE_SIZE.height) /
          SURFACE_REPORT_IMAGE_SIZE.width,
        undefined,
        "FAST",
      );
      pdf.setFontSize(8);
      pdf.text(
        "Projection along the fitted axis, not a cross-section slice.",
        PAGE.margin,
        analysisTop +
          10 +
          (contentWidth * SURFACE_REPORT_IMAGE_SIZE.height) /
            SURFACE_REPORT_IMAGE_SIZE.width,
      );
      pdf.setTextColor(100);
      pdf.text(
        `Histogram uses sampled selected points. Page ${++pageNumber} of ${pageCount}`,
        PAGE.margin,
        pageHeight - PAGE.footer,
      );
    }
    if (entry.overviewMap) {
      pdf.addPage();
      pdf.setTextColor(30);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(PAGE.titleSize);
      pdf.text(
        `${entry.title} - Location context`,
        PAGE.margin,
        PAGE.margin + 5,
      );
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(PAGE.bodySize);
      const projectLines: string[] = pdf.splitTextToSize(
        report.projectName,
        contentWidth,
      );
      pdf.text(projectLines, PAGE.margin, PAGE.margin + PAGE.mapProjectOffset);
      const mapTop =
        PAGE.margin + PAGE.mapImageOffset + projectLines.length * 4;
      pdf.text(
        entry.mapCaption ?? "Location on overview map",
        PAGE.margin,
        mapTop,
      );
      pdf.addImage(
        entry.overviewMap,
        "PNG",
        PAGE.margin,
        mapTop + 5,
        contentWidth,
        (contentWidth * SURFACE_REPORT_IMAGE_SIZE.height) /
          SURFACE_REPORT_IMAGE_SIZE.width,
        undefined,
        "FAST",
      );
      pdf.setTextColor(0, PAGE.linkGreen, 180);
      pdf.textWithLink(
        "Open orthographic view in FARO Sphere XG",
        PAGE.margin,
        pageHeight - PAGE.footer - 5,
        { url: entry.link },
      );
      pdf.setTextColor(100);
      pdf.setFontSize(8);
      pdf.text(
        `Projected selection location. Page ${++pageNumber} of ${pageCount}`,
        PAGE.margin,
        pageHeight - PAGE.footer,
      );
    }
  });
  return pdf;
}
